import { computed, ref, type ComputedRef, type Ref } from "vue";

/**
 * Cycle de vie d'une page DataTable ( datatype / referentiel ) , isole
 * dans un composable dedie pour clarifier la machine d'etat et eviter
 * la prolifration de booleans non-coordonnes dans useDataTable.ts .
 *
 * <h3>Machine d'etat</h3>
 * 2 dimensions independantes :
 * <ol>
 *   <li><b>Initialisation</b> ( one-shot ) : les deux fetches /filters
 *       et /data resolvent independamment . Le bloc Filter reste en
 *       mode "INITIAL load" tant que les deux ne sont pas resolus . Une
 *       seule resolution suffit a basculer un fetch en "Once" ; le
 *       computed <code>initialLoadComplete</code> encode l'invariant
 *       "both resolved" .</li>
 *   <li><b>Refresh courant</b> ( per-action ) : l'utilisateur peut
 *       declencher un rechargement /data avec une action explicite
 *       ( search , reset ) . Le composable expose la i18n key adaptee
 *       au refresh courant ; null hors refresh .</li>
 * </ol>
 *
 * <h3>Flag persistant <code>hasUserInteracted</code></h3>
 * Bascule a true des le 1er <code>startRefresh(...)</code> et reste
 * true jusqu'a navigation . Utilise par DataTableView pour masquer le
 * banner "About N records available" apres la 1ere interaction .
 *
 * <h3>Extension</h3>
 * Ajouter une nouvelle action ( ex "applyPreset" ) = 1 entree dans
 * <code>REFRESH_ACTIONS</code> + 1 cle i18n . Le reste ( computed
 * <code>refreshLabelKey</code> , wrappers cote useDataTable ) ne change
 * pas - Open/Closed respecte .
 *
 * <h3>Pure functions</h3>
 * Aucun side-effect reseau ; aucune dependance Vue autre que ref /
 * computed . Trivialement testable en isolation .
 */

/** Identifiant des actions de refresh supportees . */
export type RefreshActionId = "search" | "reset";

/** Specification d'une action : id + cle i18n du label affiche . */
export interface RefreshActionSpec {
  readonly id: RefreshActionId;
  readonly labelKey: string;
}

/**
 * Registre des actions de refresh . Single source of truth pour mapper
 * une action vers sa cle i18n . Ajout d'une nouvelle action = 1 entree
 * ici + 1 cle i18n dans en.json / fr.json . Aucune autre modif .
 */
export const REFRESH_ACTIONS: Readonly<Record<RefreshActionId, RefreshActionSpec>> = Object.freeze({
  search: Object.freeze({ id: "search", labelKey: "filter.searching-data" }),
  reset: Object.freeze({ id: "reset", labelKey: "filter.resetting-filters" }),
});

export interface PageLifecycle {
  // ─── Initialisation ( one-shot ) ────────────────────────────────────
  readonly filtersResolvedOnce: Ref<boolean>;
  readonly dataResolvedOnce: Ref<boolean>;
  /** <code>true</code> uniquement quand /filters ET /data ont resolu une fois . */
  readonly initialLoadComplete: ComputedRef<boolean>;

  // ─── Refresh courant ( per-action ) ─────────────────────────────────
  readonly currentRefreshAction: Ref<RefreshActionId | null>;
  /** Cle i18n du label adapte a l'action courante ; null hors refresh . */
  readonly refreshLabelKey: ComputedRef<string | null>;

  // ─── Flag persistant ────────────────────────────────────────────────
  /** Reste true des le 1er startRefresh ; jamais reset durant la session . */
  readonly hasUserInteracted: Ref<boolean>;

  // ─── Transitions ────────────────────────────────────────────────────
  markFiltersResolved: () => void;
  markDataResolved: () => void;
  startRefresh: (action: RefreshActionId) => void;
  endRefresh: () => void;
}

/**
 * Factory du cycle de vie . Un seul appel par DataTableView ( pas de
 * singleton global - chaque instance de la vue a son propre etat ) .
 */
export function usePageLifecycle(): PageLifecycle {
  const filtersResolvedOnce = ref(false);
  const dataResolvedOnce = ref(false);
  const initialLoadComplete = computed(
    () => filtersResolvedOnce.value && dataResolvedOnce.value,
  );

  const currentRefreshAction = ref<RefreshActionId | null>(null);
  const refreshLabelKey = computed<string | null>(() => {
    const action = currentRefreshAction.value;
    return action === null ? null : REFRESH_ACTIONS[action].labelKey;
  });

  const hasUserInteracted = ref(false);

  function markFiltersResolved(): void {
    filtersResolvedOnce.value = true;
  }

  function markDataResolved(): void {
    dataResolvedOnce.value = true;
  }

  function startRefresh(action: RefreshActionId): void {
    currentRefreshAction.value = action;
    hasUserInteracted.value = true;
  }

  function endRefresh(): void {
    currentRefreshAction.value = null;
  }

  return {
    filtersResolvedOnce,
    dataResolvedOnce,
    initialLoadComplete,
    currentRefreshAction,
    refreshLabelKey,
    hasUserInteracted,
    markFiltersResolved,
    markDataResolved,
    startRefresh,
    endRefresh,
  };
}
